import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OwlMascot } from "@/components/OwlMascot";
import { toast } from "sonner";
import {
  Trophy, Flame, Zap, FileText, Atom, FlaskConical, Microscope,
  Sparkles, ChevronRight, Users, Ticket
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import Leaderboard from "@/components/Leaderboard";

const SUBJECT_STYLE = {
  physics:   { icon: Atom,          tint: "bg-amber-50 text-amber-700 border-amber-200" },
  chemistry: { icon: FlaskConical,  tint: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  biology:   { icon: Microscope,    tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myClasses, setMyClasses] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  const loadClasses = async () => {
    try { const { data } = await api.get("/student/my-classes"); setMyClasses(data); }
    catch (err) { console.warn("my-classes fetch failed:", err?.message || err); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [r, e] = await Promise.all([
          api.get("/reports"),
          api.get("/experiments"),
        ]);
        setReports(r.data);
        setExperiments(e.data);
        loadClasses();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const joinClass = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/student/join-class", { code: joinCode.trim() });
      toast.success(`Joined class "${data.class.name}"!`);
      setJoinCode("");
      loadClasses();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Class code not found");
    } finally {
      setJoining(false);
    }
  };

  const nextLevel = Math.ceil(((user?.xp || 0) + 1) / 100) * 100;
  const levelProgress = ((user?.xp || 0) % 100);
  const doneIds = new Set(reports.map((r) => r.experiment_id));
  const recommendations = experiments.filter((e) => !doneIds.has(e.id)).slice(0, 4);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome hero */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-8 lg:p-10 text-white relative overflow-hidden">
          <div className="glow-blob bg-cyan-300 w-72 h-72 -bottom-16 -right-16" />
          <div className="glow-blob bg-fuchsia-400 w-52 h-52 top-0 right-40" />
          <div className="relative grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="text-cyan-200 uppercase tracking-widest text-xs font-bold mb-2">Welcome back</div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl leading-tight" data-testid="dashboard-welcome">
                Hi {user?.name?.split(" ")[0] || "Scientist"} 👋
              </h1>
              <p className="mt-3 text-blue-100 text-lg leading-relaxed max-w-xl">
                {reports.length === 0
                  ? "Let's begin with your first experiment. Brimi is ready when you are."
                  : `You've completed ${reports.length} experiment${reports.length > 1 ? "s" : ""}. Keep going!`}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/20 font-mono">
                  <Zap className="h-4 w-4 text-amber-300" /> <span className="font-bold">{user?.xp || 0}</span> XP
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/20 font-mono">
                  <Flame className="h-4 w-4 text-orange-300" /> <span className="font-bold">{user?.streak || 0}</span> day streak
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/20 font-mono">
                  <Trophy className="h-4 w-4 text-yellow-300" /> <span className="font-bold">{user?.badges?.length || 0}</span> badges
                </div>
              </div>

              <div className="mt-6 max-w-md">
                <div className="flex justify-between text-xs mb-1.5 font-mono text-blue-100">
                  <span>Level {Math.floor((user?.xp || 0) / 100) + 1}</span>
                  <span>{user?.xp || 0} / {nextLevel}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full bg-cyan-300 rounded-full transition-all" style={{ width: `${levelProgress}%` }} />
                </div>
              </div>
            </div>
            <div className="hidden lg:block text-center">
              <OwlMascot size={160} talking />
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {["physics", "chemistry", "biology"].map((s) => {
            const S = SUBJECT_STYLE[s];
            return (
              <Link key={s} to={`/labs/${s}`} data-testid={`dash-lab-${s}`}>
                <div className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${S.tint}`}>
                      <S.icon className="h-6 w-6" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                  <div className="mt-4 font-outfit font-bold text-xl text-slate-900 capitalize">{s}</div>
                  <div className="text-sm text-slate-500 mt-1">
                    {experiments.filter((e) => e.subject === s).length} experiments
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Leaderboard */}
        <div className="mt-8">
          <Leaderboard classCode={myClasses?.[0]?.code || null} />
        </div>

        {/* Badges */}
        {user?.badges?.length > 0 && (
          <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="font-outfit font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
              <Trophy className="h-5 w-5 text-amber-500" /> Your badges
            </div>
            <div className="flex flex-wrap gap-2">
              {user.badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700" data-testid={`badge-${b.replace(/\s+/g, '-')}`}>
                  <Sparkles className="h-3 w-3" /> {b}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Classes section */}
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-outfit font-bold text-lg text-slate-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" /> My classes
            </div>
          </div>
          {myClasses.length === 0 ? (
            <div className="text-slate-500 text-sm mb-4">Not in any class yet. Enter a code from your teacher below.</div>
          ) : (
            <div className="flex flex-wrap gap-2 mb-4">
              {myClasses.map((c) => (
                <div key={c.class_id} className="px-3 py-2 rounded-xl bg-blue-50 border border-blue-200" data-testid={`student-class-${c.class_id}`}>
                  <div className="font-bold text-slate-900 text-sm">{c.name}</div>
                  <div className="font-mono text-xs text-blue-600">{c.code}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Class code (e.g. IX-PHY-XR7)" className="h-10 font-mono uppercase" data-testid="student-join-code-input" />
            <Button onClick={joinClass} disabled={joining} className="bg-blue-600 hover:bg-blue-700 h-10 rounded-lg" data-testid="student-join-class-btn">
              <Ticket className="mr-2 h-4 w-4" /> Join
            </Button>
          </div>
        </div>

        {/* Recommendations */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-outfit font-bold text-lg text-slate-900">Recommended for you</div>
              <Link to="/labs" className="text-sm text-blue-600 font-semibold">All labs →</Link>
            </div>
            {loading ? (
              <div className="text-slate-400 font-mono text-sm">Loading…</div>
            ) : recommendations.length === 0 ? (
              <div className="text-slate-500">You&apos;ve done them all! 🎉</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {recommendations.map((e) => {
                  const S = SUBJECT_STYLE[e.subject];
                  return (
                    <Link key={e.id} to={`/experiment/${e.id}`} data-testid={`rec-exp-${e.id}`}>
                      <div className="p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border ${S.tint}`}>
                            <S.icon className="h-4 w-4" />
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Class {e.grade}</span>
                        </div>
                        <div className="font-semibold text-slate-900 leading-tight">{e.title}</div>
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2">{e.short}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent reports */}
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="font-outfit font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-500" /> Recent reports
            </div>
            {reports.length === 0 ? (
              <div className="text-slate-500 text-sm">No reports yet. Finish an experiment to auto-generate one.</div>
            ) : (
              <div className="space-y-2">
                {reports.slice(0, 6).map((r) => (
                  <Link key={r.report_id} to={`/report/${r.report_id}`} data-testid={`report-${r.report_id}`}>
                    <div className="p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors">
                      <div className="text-sm font-semibold text-slate-900 leading-tight">{r.experiment_title}</div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;
